/* eslint-disable react-hooks/exhaustive-deps */
import { ITEM_SCENE_SCALE } from '@constants'
import { useAppStore } from '@store'
import { AnyID, Item } from '@types'
import { toVector3 } from '@utils/conversion/toVector3'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Vector3 } from 'three'

export function useItemScale(id: AnyID, morphs: Item['morphs'], glbSize: Item['glbSize']) {
  const sizeRef = useRef(null)
  const sceneBoundingBoxes = useAppStore.use.sceneBoundingBoxes()
  const meshBBInfo = sceneBoundingBoxes.get(id)!
  const editGLBSize = useAppStore.use.editGLBSize()
  const isModalOpen = useAppStore.use.isModalOpen()
  const item = useAppStore.use.items().get(id)

  const [scaleVector, setScaleVector] = useState<Vector3>(() => computeScaleVector(morphs))
  const [isFirstRender, setIsFirstRender] = useState(true)
  useEffect(() => setIsFirstRender(false), [])

  useLayoutEffect(() => {
    const newScaleVector = computeScaleVector(morphs)
    setScaleVector(newScaleVector)
  }, [morphs])

  useLayoutEffect(() => {
    if (isModalOpen) if (isInitialiazionPending()) return
    let newGLBSizeVector

    const itemBB = meshBBInfo?.boundingBox
    if (!itemBB) return
    const newOBBSizeVector = toVector3([scaleVector.x / 2, scaleVector.y / 2, scaleVector.z / 2])
    const [xProp, yProp, zProp] = morphs
    if (item?.hasMorphs) {
      newGLBSizeVector = toVector3([1, 1, 1])
    } else {
      newGLBSizeVector = toVector3([
        (xProp.default / ITEM_SCENE_SCALE) * scaleVector.x,
        (yProp.default / ITEM_SCENE_SCALE) * scaleVector.y,
        (zProp.default / ITEM_SCENE_SCALE) * scaleVector.z,
      ])
    }
    itemBB.halfSize.set(newOBBSizeVector.x, newOBBSizeVector.y, newOBBSizeVector.z)
    editGLBSize(id, newGLBSizeVector)
  }, [...scaleVector, isModalOpen])

  function isInitialiazionPending() {
    return (
      !meshBBInfo ||
      !scaleVector.x ||
      !scaleVector.y ||
      !scaleVector.z ||
      !glbSize.x ||
      !glbSize.y ||
      !glbSize.z
    )
  }

  function computeScaleVector(morphs: Item['morphs']) {
    const [xProp, yProp, zProp] = morphs
    if (item?.hasMorphs) return toVector3([1, 1, 1])
    if (morphs.length !== 3) return toVector3([1, 1, 1])
    return toVector3([
      (xProp?._default ?? xProp?.default) / xProp?.default ?? glbSize.x,
      (yProp?._default ?? yProp?.default) / yProp?.default ?? glbSize.y,
      (zProp?._default ?? zProp?.default) / zProp?.default ?? glbSize.z,
    ])
  }

  return { sizeRef, scaleVector }
}
